import ShowChartToolCall from '../components/displayTools/ShowChartToolCall';
import ShowDatasourceToolCall from '../components/displayTools/ShowDatasourceToolCall';
import AgentSessionToolCall from '../components/tools/AgentSessionToolCall';
import CreateDatasourceToolCall from '../components/tools/CreateDatasourceToolCall';
import CreateWatchCompaniesToolCall from '../components/tools/CreateWatchCompaniesToolCall';
import FetchDataToolCall from '../components/tools/FetchDataToolCall';
import ImpactAnalysisToolCall from '../components/tools/ImpactAnalysisToolCall';
import ListValueChainsToolCall from '../components/tools/ListValueChainsToolCall';
import LookUpDocumentToolCall from '../components/tools/LookUpDocumentToolCall';
import LookupDossierPublicFactsToolCall from '../components/tools/LookupDossierPublicFactsToolCall';
import LookupPublicCompanyToolCall from '../components/tools/LookupPublicCompanyToolCall';
import LookupStandardsToolCall from '../components/tools/LookupStandardsToolCall';
import MonitoringSessionToolCall from '../components/tools/MonitoringSessionToolCall';
import SearchWatchCompaniesToolCall from '../components/tools/SearchWatchCompaniesToolCall';
import ShareDocumentToolCall from '../components/tools/ShareDocumentToolCall';
import StartAgentSearchToolCall from '../components/tools/StartAgentSearchToolCall';
import StartImpactAnalysisToolCall from '../components/tools/StartImpactAnalysisToolCall';
import StartMonitoringSessionToolCall from '../components/tools/StartMonitoringSessionToolCall';
import WriteDocumentToolCall from '../components/tools/WriteDocumentToolCall';

import type { ComponentType } from 'react';
import type { IAiChatToolCallOf } from '../interfaces/IAiChatToolCall';
import type { TAiChatToolName } from '../interfaces/IAiChatTools';

/** Each tool the FE knows may have a component, and that component takes the part typed for it. */
type TToolComponents = { [NAME in TAiChatToolName]?: ComponentType<IAiChatToolCallOf<NAME>> };

/**
 * Components for the tools that render as more than a name, keyed by tool name.
 *
 * Any tool the API calls that is not listed here falls back to the name row. Giving one tool a
 * custom view is one entry here and nothing else — the message body never branches on the tool.
 * A component registered under a name must take `IAiChatToolCallOf<that name>`. A tool that is a
 * display slot rather than a step is also listed in `DISPLAY_TOOLS`, and lives under `displayTools/`.
 *
 * Built without a prototype: the key is a tool name the API chose, and on a plain object a tool
 * called `constructor` would look up a function that React cannot render.
 *
 * @see [12.2 Show the tool on the frontend](../../../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
const TOOL_COMPONENTS: TToolComponents = Object.create(null);

TOOL_COMPONENTS.lookUpDocument = LookUpDocumentToolCall;
TOOL_COMPONENTS.writeDocument = WriteDocumentToolCall;
TOOL_COMPONENTS.shareDocument = ShareDocumentToolCall;
TOOL_COMPONENTS.listValueChains = ListValueChainsToolCall;
TOOL_COMPONENTS.lookupStandards = LookupStandardsToolCall;
TOOL_COMPONENTS.lookupPublicCompany = LookupPublicCompanyToolCall;
TOOL_COMPONENTS.lookupDossierPublicFacts = LookupDossierPublicFactsToolCall;
TOOL_COMPONENTS.searchWatchCompanies = SearchWatchCompaniesToolCall;
TOOL_COMPONENTS.createWatchCompanies = CreateWatchCompaniesToolCall;
TOOL_COMPONENTS.fetchData = FetchDataToolCall;
TOOL_COMPONENTS.createDatasource = CreateDatasourceToolCall;
TOOL_COMPONENTS.showDatasource = ShowDatasourceToolCall;
TOOL_COMPONENTS.showChart = ShowChartToolCall;
// One component for the two: a created and an updated analysis are answered in the same shape.
TOOL_COMPONENTS.createImpactAnalysis = ImpactAnalysisToolCall;
TOOL_COMPONENTS.updateImpactAnalysis = ImpactAnalysisToolCall;
TOOL_COMPONENTS.startImpactAnalysis = StartImpactAnalysisToolCall;
// One component for the two: a created session and changed tabs are answered in the same shape.
TOOL_COMPONENTS.createAgentSession = AgentSessionToolCall;
TOOL_COMPONENTS.updateAgentSessionUnits = AgentSessionToolCall;
TOOL_COMPONENTS.startAgentSearch = StartAgentSearchToolCall;
// One component for the two: a created session and changed units are answered in the same shape.
TOOL_COMPONENTS.createMonitoringSession = MonitoringSessionToolCall;
TOOL_COMPONENTS.updateMonitoringSessionUnits = MonitoringSessionToolCall;
TOOL_COMPONENTS.startMonitoringSession = StartMonitoringSessionToolCall;

export default TOOL_COMPONENTS;
